/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { SearchBar } from '~components/atoms/SearchBar/SearchBar';
import { AboveNavMobile } from '~components/organisms/mobile/AboveNavMobile/AboveNavMobile';
import SearchContextProvider from '~src/contexts/SearchContext';
import { FileList } from './mobile/FileList';
import { HomeMobile } from './mobile/HomeMobile';
import FileVersionsContext, {
  FileVersionsCtx,
} from '~src/contexts/FileVersionsContext';
import { Footer } from '~components/organisms/mobile/Footer/Footer';
import { ModalsProvider } from '~src/contexts/ModalsContext';
import { ActionModalsContainer } from '~components/molecules/ActionModalsContainer/ActionModalsContainer';
import UploadContext, { UploadCtx } from '~src/contexts/UploadContext';
import { FavouritesList } from './mobile/FavouritesList';
import { ModuleFileList } from './mobile/ModuleFileList';
import { apiCweb, getNewToken } from '~src/services/api/api-cweb';
import { useMutation } from 'react-query';
import { useIsPath } from '~src/hooks/useIsPath';
import UploadPanel from '~components/atoms/UploadPanel/UploadPanel';
import ResizeObserver from 'resize-observer-polyfill';
import { debounce } from 'lodash';
import FileProgressContext, {
  FileProgressCtx,
} from '~src/contexts/FileProgressContext';
import { FileProgressPanel } from '~components/molecules/FileProgressPanel/FileProgressPanel';
import { useProject } from '~src/hooks/api-hooks/useProject';
import { FileToViewProvider } from '~src/contexts/FileToViewContext';
import { MobileViewerHost } from '~components/atoms/Viewers/SVFViewer/MobileViewerHost';

interface ProjectsRootMobileProps {
  test?: boolean;
}

export const ProjectsRootMobile: React.FC<ProjectsRootMobileProps> = ({}) => {
  const history = useHistory();
  const isOnArchive = useIsPath('/archive');
  const isOnModule = useIsPath('/module');
  const isOnFavorites = useIsPath('/favourites');
  const project = useProject();
  // const renderArchive = (props) => {

  // }
  // context of selected file versions
  const zoneDivRef = useRef<HTMLDivElement>(null);

  const [bottomViewHeight, setBottomViewHeight] = useState(0);
  const [showSearch, setShowSearch] = useState(false);

  const [fileVersionsContext, setFileVersionsContext] = useState<
    FileVersionsCtx['fileVersionsContext']
  >([]);
  const fileVersionsContextChanged = useMemo(
    () => ({ fileVersionsContext, setFileVersionsContext }),
    [fileVersionsContext, setFileVersionsContext]
  );
  const resizeObserverRef = useRef(
    new ResizeObserver(
      debounce(([resizeEntry]) => {
        // update `bottomViewHeight` based on "zoneDiv" height
        setBottomViewHeight(resizeEntry.contentRect.height);
      }, 0)
    )
  );

  const [uploadContext, setUploadContext] = useState<
    UploadCtx['uploadContext']
  >([]);
  const uploadContextChanged = useMemo(
    () => ({ uploadContext, setUploadContext }),
    [uploadContext, setUploadContext]
  );

  const [fileProgressContext, setFileProgressContext] = useState<
    FileProgressCtx['fileProgressContext']
  >([]);
  const fileProgressChanged = useMemo(
    () => ({ fileProgressContext, setFileProgressContext }),
    [fileProgressContext, setFileProgressContext]
  );

  useEffect(() => {
    const div = zoneDivRef.current;

    if (div) {
      const resizeObserver = resizeObserverRef.current; // ResizeObserver instance
      resizeObserver.observe(div); // add listener

      // cleanup -> remove listener
      return () => resizeObserver.unobserve(div);
    }
  }, [uploadContext.length]);

  // context of files ready to be uploaded

  const [doGetNewToken, getNewTokenMutation] = useMutation(getNewToken);

  useEffect(() => {
    if (!apiCweb.defaults.headers.common.Authorization) {
      doGetNewToken();
    }
  }, [doGetNewToken]);

  // const tableScroll: Required<FileTableProps>['scroll'] = {
  // satisfies sum of fixed width columns + reasonable dynamic "name" column
  // x: 1065,
  // 141px is topBar + folderNameBar + tableHeader + scrollbar
  // y: `calc(100vh - 141px - ${bottomViewHeight}px)`,
  // };

  return (
    <div css={{ display: 'flex', flexDirection: 'column' }}>
      {/* Provide viewer context to all mobile routes (FileListItem uses it) */}
      <FileToViewProvider>
        <SearchContextProvider>
          <ModalsProvider>
            <FileProgressContext.Provider value={fileProgressChanged}>
              <FileVersionsContext.Provider value={fileVersionsContextChanged}>
                <UploadContext.Provider value={uploadContextChanged}>
                  <AboveNavMobile />
                  {showSearch &&
                    (isOnFavorites || isOnModule || isOnArchive) && (
                      <SearchBar mobile />
                    )}
                  <div
                    style={{
                      height:
                        showSearch &&
                        (isOnFavorites || isOnModule || isOnArchive)
                          ? // 170 = header(60) + footer(60) + search bar(50px)
                            `calc(100vh - 170px  - ${
                              uploadContext.length ? bottomViewHeight : 0
                            }px)`
                          : // 120 = header(60) + footer(60)
                            `calc(100vh - 120px - ${
                              uploadContext.length ? bottomViewHeight : 0
                            }px) `,
                      overflow: 'scroll',
                    }}>
                    <Switch>
                      <Route
                        path="/projects/:projectId"
                        exact
                        component={HomeMobile}
                      />
                      <Route
                        path="/projects/:projectId/archive/:folderId"
                        // component={FileList}
                        render={(props) => <FileList />}
                      />
                      <Route
                        path="/projects/:projectId/archive/"
                        render={(props) => <FileList />}
                      />
                      <Route
                        path="/projects/:projectId/favourites"
                        render={(props) => <FavouritesList />}
                      />

                      <Route
                        path="/projects/:projectId/module/:moduleId"
                        render={(props) => <ModuleFileList />}
                      />
                    </Switch>
                  </div>

                  {uploadContext.length > 0 && (
                    <div ref={zoneDivRef}>
                      <UploadPanel key="upload" mobile={true} />
                    </div>
                  )}
                  {fileProgressContext.length > 0 && (
                    <FileProgressPanel mobile={true} />
                  )}

                  <Footer setShowSearch={setShowSearch} />

                  {/* Mount fullscreen viewer when a 3D file is selected */}
                  <MobileViewerHost />
                </UploadContext.Provider>
              </FileVersionsContext.Provider>
              <ActionModalsContainer />
            </FileProgressContext.Provider>
          </ModalsProvider>
        </SearchContextProvider>
      </FileToViewProvider>
    </div>
  );
};
